export type StyleKey = 'driver' | 'expressive' | 'amiable' | 'analytic';

export interface StyleDescription {
  key: StyleKey;
  name: string;
  nickname: string;
  color: string;
  darkColor: string;
  lightColor: string;
  tailwindBg: string;
  tailwindBorder: string;
  tailwindText: string;
  tailwindLightBg: string;
  summary: string;
  traits: string[];
  positivePerceptions: string[];
  stressPerceptions: string[];
  highWants: string[];
}

export const STYLES: Record<StyleKey, StyleDescription> = {
  driver: {
    key: 'driver',
    name: 'Driver',
    nickname: 'The Doer',
    color: '#C0392B',
    darkColor: '#922B21',
    lightColor: '#FADBD8',
    tailwindBg: 'bg-driver',
    tailwindBorder: 'border-driver',
    tailwindText: 'text-driver',
    tailwindLightBg: 'bg-driver-light',
    summary:
      'Action-oriented and decisive. You are a problem solver who leads with confidence, gets results, and pushes through obstacles. You are direct, assertive, and highly competitive.',
    traits: [
      'Action-oriented',
      'Decisive',
      'Problem solver',
      'Direct',
      'Assertive',
      'Results-oriented',
      'Competitive',
      'Independent',
    ],
    positivePerceptions: ['Strong-willed', 'Practical', 'Efficient', 'Independent', 'Decisive'],
    stressPerceptions: ['Pushy', 'Dominating', 'Harsh', 'Severe', 'Tough'],
    highWants: ['Challenges', 'Authority', 'Power', 'Freedom from controls', 'Options'],
  },
  expressive: {
    key: 'expressive',
    name: 'Expressive',
    nickname: 'The Intuitor',
    color: '#E67E22',
    darkColor: '#CA6F1E',
    lightColor: '#FDEBD0',
    tailwindBg: 'bg-expressive',
    tailwindBorder: 'border-expressive',
    tailwindText: 'text-expressive',
    tailwindLightBg: 'bg-expressive-light',
    summary:
      'Verbal, motivating, and enthusiastic. You inspire others with your energy, charm, and optimism. You are convincing, impulsive, and thrive on social connection and recognition.',
    traits: [
      'Verbal',
      'Motivating',
      'Enthusiastic',
      'Convincing',
      'Charming',
      'Inspiring',
      'Optimistic',
      'Animated',
    ],
    positivePerceptions: ['Ambitious', 'Stimulating', 'Enthusiastic', 'Dramatic', 'Friendly'],
    stressPerceptions: ['Manipulative', 'Excitable', 'Undisciplined', 'Egotistical', 'Reacting'],
    highWants: ['Social recognition', 'Freedom from details', 'Group activities', 'Personal approach'],
  },
  amiable: {
    key: 'amiable',
    name: 'Amiable',
    nickname: 'The Feeler',
    color: '#27AE60',
    darkColor: '#1E8449',
    lightColor: '#D5F5E3',
    tailwindBg: 'bg-amiable',
    tailwindBorder: 'border-amiable',
    tailwindText: 'text-amiable',
    tailwindLightBg: 'bg-amiable-light',
    summary:
      'Patient, loyal, and empathetic. You are the team\'s anchor — supportive, stable, and deeply attuned to the needs of others. You build trust through consistency and genuine care.',
    traits: [
      'Patient',
      'Loyal',
      'Sympathetic',
      'Supportive',
      'Stable',
      'Considerate',
      'Empathetic',
      'Trusting',
    ],
    positivePerceptions: ['Supportive', 'Respectful', 'Dependable', 'Agreeable', 'Willing'],
    stressPerceptions: ['Conforming', 'Unsure', 'Pliable', 'Dependent', 'Awkward'],
    highWants: ['Guarantees', 'Security', 'Appreciation', 'Specialization', 'Quality control'],
  },
  analytic: {
    key: 'analytic',
    name: 'Analytic',
    nickname: 'The Analyzer',
    color: '#2980B9',
    darkColor: '#1A5276',
    lightColor: '#D6EAF8',
    tailwindBg: 'bg-analytic',
    tailwindBorder: 'border-analytic',
    tailwindText: 'text-analytic',
    tailwindLightBg: 'bg-analytic-light',
    summary:
      'Diplomatic, accurate, and conscientious. You approach every problem systematically, valuing data, precision, and logic above all. You bring order, rigor, and high standards to everything you do.',
    traits: [
      'Diplomatic',
      'Accurate',
      'Systematic',
      'Logical',
      'Analytical',
      'Precise',
      'Disciplined',
      'Cautious',
    ],
    positivePerceptions: ['Industrious', 'Persistent', 'Serious', 'Exacting', 'Orderly'],
    stressPerceptions: ['Critical', 'Indecisive', 'Stuffy', 'Picky', 'Moralistic'],
    highWants: ['High standards', 'Details', 'Perfection', 'Traditional procedures', 'Accuracy'],
  },
};

export const STYLE_ORDER: StyleKey[] = ['driver', 'expressive', 'amiable', 'analytic'];
